import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertCircle, Handshake, RefreshCw, Search, ShieldAlert, X } from 'lucide-react'
import { useGetOwnerBoard } from '../hooks/backend/owners'
import { useCapabilities } from '../hooks/useCapabilities'
import { Spinner } from '../lib/shadcn/spinner'
import { Input } from '../lib/shadcn/input'
import { Button } from '../lib/shadcn/button'
import { cn } from '../lib/shadcn/utils'
import { OwnerBoardGroups } from '../components/owners/OwnerBoardGroups'
import type { OwnerRow } from '../utils/owners'
import {
  CLOSED_STAGES,
  PARKED_STAGES,
  SEGMENTS,
  STAGE_ORDER,
  TRACKS,
  type SegmentCode,
  inSegment,
  stageLabel,
  stageTone,
  summarise,
  trackOf,
} from '../utils/ownerBoard'

type TrackTab = 'all' | 'asset_finance' | 'brought_in' | 'brings_own_vehicle'

const TRACK_TABS: TrackTab[] = ['asset_finance', 'brought_in', 'brings_own_vehicle', 'all']

const TONE_DOT: Record<string, string> = {
  green: 'bg-emerald-500',
  red: 'bg-rose-500',
  amber: 'bg-amber-500',
  brand: 'bg-[hsl(var(--brand))]',
  neutral: 'bg-muted-foreground/50',
}

function isTrackTab(v: string | null): v is TrackTab {
  return v === 'all' || v === 'asset_finance' || v === 'brought_in' || v === 'brings_own_vehicle'
}

export default function Owners() {
  const { loading: accessLoading, canView, canEditOwners, caption } = useCapabilities()

  // Track, stage and search live in the URL so a link opens the same view.
  const [params, setParams] = useSearchParams()
  const qParam = params.get('q')
  const trackParam = params.get('track')
  const track: TrackTab = isTrackTab(trackParam) ? trackParam : 'asset_finance'
  const [search, setSearch] = useState(qParam ?? '')
  useEffect(() => {
    if (qParam != null) setSearch(qParam)
  }, [qParam])
  const [segment, setSegment] = useState<SegmentCode>('all')
  const [stages, setStages] = useState<string[]>(() => {
    const s = params.get('stage')
    return s ? s.split(',').filter(Boolean) : []
  })

  const setTrack = (next: TrackTab) => {
    const p = new URLSearchParams(params)
    p.set('track', next)
    p.delete('stage')
    p.delete('q')
    setParams(p, { replace: true })
    setStages([])
    setSegment('all')
  }

  const toggleStage = (code: string) => {
    const next = stages.includes(code) ? stages.filter((s) => s !== code) : [...stages, code]
    setStages(next)
    const p = new URLSearchParams(params)
    if (next.length) p.set('stage', next.join(','))
    else p.delete('stage')
    setParams(p, { replace: true })
  }

  const board = useGetOwnerBoard()
  const trigger = board.trigger
  const load = useCallback(() => {
    trigger({}, { skipCache: true })
  }, [trigger])
  useEffect(() => {
    if (canView) load()
  }, [canView, load])

  const allRows = useMemo(() => {
    const d = board.data as OwnerRow[] | undefined
    return Array.isArray(d) ? d : []
  }, [board.data])

  const term = search.trim().toLowerCase()
  const searching = term.length > 0

  // Per-track summaries for the cards. Always over every row.
  const summaries = useMemo(() => {
    const out: Record<TrackTab, ReturnType<typeof summarise>> = {
      all: summarise(allRows.map(asBoardRow)),
      asset_finance: summarise([]),
      brought_in: summarise([]),
      brings_own_vehicle: summarise([]),
    }
    for (const t of TRACKS) {
      out[t.code] = summarise(allRows.filter((r) => r.owner_model === t.code).map(asBoardRow))
    }
    return out
  }, [allRows])

  // Rows for the chosen track. A search looks across every track, because the
  // question "where is this person" should not depend on which tab is open.
  const trackRows = useMemo(() => {
    if (searching) {
      return allRows.filter((r) =>
        [r.full_name, r.primary_email, r.primary_phone, r.live_plates, r.all_live_plates]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term),
      )
    }
    if (track === 'all') return allRows
    return allRows.filter((r) => r.owner_model === track)
  }, [allRows, track, searching, term])

  const segmentRows = useMemo(
    () => trackRows.filter((r) => inSegment(asBoardRow(r), segment)),
    [trackRows, segment],
  )

  // Stage chips: the track's own stages always (so an empty track shows its
  // shape), then whatever else has people in it.
  const stageChips = useMemo(() => {
    const counts = new Map<string, number>()
    for (const r of segmentRows) counts.set(r.current_status, (counts.get(r.current_status) ?? 0) + 1)
    const base = !searching && track !== 'all' ? trackOf(track)?.stages ?? [] : []
    const codes = new Set<string>(base)
    for (const c of counts.keys()) codes.add(c)
    for (const c of [...PARKED_STAGES, ...CLOSED_STAGES]) if (counts.get(c)) codes.add(c)
    const order = (s: string) => {
      const i = STAGE_ORDER.indexOf(s)
      return i === -1 ? 999 : i
    }
    return [...codes].sort((a, b) => order(a) - order(b)).map((code) => ({ code, count: counts.get(code) ?? 0 }))
  }, [segmentRows, track, searching])

  const visibleRows = useMemo(
    () => (stages.length ? segmentRows.filter((r) => stages.includes(r.current_status)) : segmentRows),
    [segmentRows, stages],
  )

  const activeTrack = track === 'all' ? undefined : trackOf(track)

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">The Owners view is available to admins only.</p>
          <p className="text-xs text-muted-foreground">Contact an admin if you need access to owner applications.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
              <Handshake className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-foreground">Owners</h1>
              <p className="text-xs text-muted-foreground">{caption}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto h-8 gap-1.5"
              onClick={load}
              disabled={board.loading}
            >
              {board.loading ? <Spinner className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
          </div>

          {/* Track cards, highest first */}
          <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
            {TRACK_TABS.map((code) => {
              const t = code === 'all' ? undefined : trackOf(code)
              const s = summaries[code]
              const active = !searching && track === code
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    setSearch('')
                    setTrack(code)
                  }}
                  className={cn(
                    'rounded-lg border p-3 text-left transition-colors',
                    active
                      ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))]'
                      : 'border-border bg-background hover:border-foreground/30',
                  )}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        active ? 'text-[hsl(var(--brand-soft-foreground))]' : 'text-foreground',
                      )}
                    >
                      {t ? t.label : 'All owners'}
                    </span>
                    <span className="text-lg font-semibold tabular-nums text-foreground">
                      {board.loading && allRows.length === 0 ? '…' : s.total}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">
                    {s.open} in progress · {s.live} live
                    {s.stale > 0 && <span className="text-amber-700 dark:text-amber-300"> · {s.stale} stuck</span>}
                  </p>
                </button>
              )
            })}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            One row per person on each track they are in. Someone in Fleetly more than one way shows on
            each of those tracks. Live means a car in the fleet on the vehicle register.
          </p>
        </div>

        {/* Search and segments */}
        <div className="space-y-2 border-b border-border px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                // A stage picked on one tab would silently empty the search
                // results, which read as "this person does not exist".
                if (stages.length) {
                  setStages([])
                  const p = new URLSearchParams(params)
                  p.delete('stage')
                  setParams(p, { replace: true })
                }
              }}
              placeholder="Find anyone, in any track: name, email, phone or plate"
              className="pl-9 pr-9"
            />
            {searching && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {SEGMENTS.map((sg) => {
              const n = trackRows.filter((r) => inSegment(asBoardRow(r), sg.code)).length
              const active = segment === sg.code
              return (
                <button
                  key={sg.code}
                  type="button"
                  title={sg.help}
                  onClick={() => setSegment(sg.code)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                    active
                      ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {sg.label} <span className="tabular-nums opacity-70">{n}</span>
                </button>
              )
            })}
          </div>
          {searching ? (
            <p className="text-xs text-muted-foreground">Searching every track.</p>
          ) : activeTrack ? (
            <p className="text-xs text-muted-foreground">{activeTrack.blurb}</p>
          ) : null}
        </div>

        {/* Stage strip: click a stage to show only that stage */}
        {stageChips.length > 0 && (
          <div className="border-b border-border px-4 py-2.5">
            <div className="flex flex-wrap gap-1.5">
              {stageChips.map((s) => {
                const active = stages.includes(s.code)
                return (
                  <button
                    key={s.code}
                    type="button"
                    onClick={() => toggleStage(s.code)}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors',
                      active
                        ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]'
                        : s.count === 0
                          ? 'border-dashed border-border text-muted-foreground/70'
                          : 'border-border text-foreground hover:border-foreground/30',
                    )}
                  >
                    <span className={cn('h-1.5 w-1.5 rounded-full', TONE_DOT[stageTone(s.code)])} />
                    {stageLabel(s.code)}
                    <span className="font-semibold tabular-nums">{s.count}</span>
                  </button>
                )
              })}
              {stages.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setStages([])
                    const p = new URLSearchParams(params)
                    p.delete('stage')
                    setParams(p, { replace: true })
                  }}
                  className="px-2 py-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                >
                  Show all stages
                </button>
              )}
            </div>
          </div>
        )}

        {/* Groups */}
        {board.error ? (
          <div className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{board.error}</span>
          </div>
        ) : board.loading && allRows.length === 0 ? (
          <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4" /> Loading owners…
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-center text-sm text-muted-foreground">
            <Handshake className="h-6 w-6" />
            {track === 'brought_in' && !searching && trackRows.length === 0 ? (
              <>
                <span>
                  Nobody is on the new model track yet. Owners arrive here from the Manage My Car screener on
                  fleetly.co.za. To add someone by hand, open them on the old model, choose Edit and set the
                  track to Car to new model.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-1"
                  onClick={() => {
                    setTrack('brings_own_vehicle')
                    setSegment('live')
                  }}
                >
                  See the {summaries.brings_own_vehicle.live} live old model owners
                </Button>
              </>
            ) : (
              'Nobody matches this view.'
            )}
          </div>
        ) : (
          <OwnerBoardGroups
            rows={visibleRows}
            showTrack={searching || track === 'all'}
            forceOpen={stages}
            canEditOwners={canEditOwners}
            onReload={load}
          />
        )}
      </div>
    </div>
  )
}

function asBoardRow(r: OwnerRow) {
  return { owner_model: r.owner_model, current_status: r.current_status, days_in_stage: r.days_in_stage ?? null }
}
